import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { FiEye, FiEyeOff, FiCheckCircle } from 'react-icons/fi';
import ApiServices from '../../services/apiservices';

const ChangePassword = () => {
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const toggleVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const initialValues = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required('Current password is required'),
    newPassword: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .required('New password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
      .required('Please confirm your new password'),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const res = await ApiServices.putRequest('/user/changepassword.php', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });

      if (res.success) {
        toast.success("Password updated successfully!");
        resetForm();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-6 bg-gray-50">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
      >
        <div className="p-8 text-gray-900 text-center">
         
          <h2 className="text-2xl font-bold">Change Password</h2>
        </div>

        <div className="p-8">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, errors, touched }) => (
              <Form className="space-y-5">
                {/* Current Password */}
                <div className="space-y-1">
                  <label className="text-sm font-bold text-gray-700 ml-1">Current Password</label>
                  <div className="relative">
                    <Field
                      type={showPassword.current ? "text" : "password"}
                      name="currentPassword"
                      className={`w-full pl-4 pr-10 py-3 border rounded-xl outline-none transition-all ${
                        errors.currentPassword && touched.currentPassword 
                        ? "border-red-500 focus:ring-1 ring-red-500" 
                        : "focus:border-blue-500 focus:ring-1 ring-blue-500"
                      }`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => toggleVisibility('current')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600"
                    >
                      {showPassword.current ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                  <ErrorMessage name="currentPassword" component="div" className="text-red-500 text-xs mt-1 ml-1 font-medium" />
                </div>

                <hr className="border-gray-100 my-2" />

                {/* New Password */}
                <div className="space-y-1">
                  <label className="text-sm font-bold text-gray-700 ml-1">New Password</label>
                  <div className="relative">
                    <Field
                      type={showPassword.new ? "text" : "password"}
                      name="newPassword"
                      className={`w-full pl-4 pr-10 py-3 border rounded-xl outline-none transition-all ${
                        errors.newPassword && touched.newPassword 
                        ? "border-red-500 focus:ring-1 ring-red-500" 
                        : "focus:border-blue-500 focus:ring-1 ring-blue-500"
                      }`}
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => toggleVisibility('new')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600"
                    >
                      {showPassword.new ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                  <ErrorMessage name="newPassword" component="div" className="text-red-500 text-xs mt-1 ml-1 font-medium" />
                </div>

                {/* Confirm Password */}
                <div className="space-y-1">
                  <label className="text-sm font-bold text-gray-700 ml-1">Confirm New Password</label>
                  <div className="relative">
                    <Field
                      type={showPassword.confirm ? "text" : "password"}
                      name="confirmPassword"
                      className={`w-full pl-4 pr-10 py-3 border rounded-xl outline-none transition-all ${
                        errors.confirmPassword && touched.confirmPassword 
                        ? "border-red-500 focus:ring-1 ring-red-500" 
                        : "focus:border-blue-500 focus:ring-1 ring-blue-500"
                      }`}
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => toggleVisibility('confirm')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600"
                    >
                      {showPassword.confirm ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                  <ErrorMessage name="confirmPassword" component="div" className="text-red-500 text-xs mt-1 ml-1 font-medium" />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gray-500 hover:bg-gray-900 text-white py-3 rounded-xl font-bold shadow-lg shadow-blue-200 transition-all flex items-center justify-center gap-2 mt-4 active:scale-95 disabled:opacity-70"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <FiCheckCircle /> Update Password
                    </>
                  )}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </motion.div>
    </div>
  );
};

export default ChangePassword;